/**
 * USR-003 P-UI Phase B (spec D4) — the one check for every `?next=` the app reads: the login page,
 * the auth callback and the forced password change. SEC-002 introduced it inside the login page
 * (`safePostLoginPath`); it moved here so all readers use the same rules.
 *
 * SECURITY — `next` is an open-redirect surface on a real login page, which makes it a credible
 * phishing vector: a link to `pug-med.com/login?next=https://evil.example/login` looks legitimate
 * and lands on a fake right after the user typed a password. So the value is hostile input. It is
 * accepted only when, both as given and once percent-decoded, it:
 *
 *   - starts with a single '/' (rejects every absolute form, scheme and bare host);
 *   - does not start with '//' (protocol-relative) or '/\' (browsers turn '\' into '/');
 *   - contains no '\' at all, no whitespace and no control characters (the usual smuggling);
 *   - has no ':' before its first '/' (no scheme);
 *   - is at most MAX_NEXT_LENGTH characters.
 *
 * It must then resolve, under the URL parser, to the same origin as a dummy base, and it is
 * returned RE-SERIALISED (path + query + hash from the parser), not as typed. `/login` itself is
 * refused so sign-in never bounces back to sign-in. Anything else gives DEFAULT_POST_LOGIN.
 *
 * Pure: no `window`, so the server callback and the client pages share it.
 */

export const DEFAULT_POST_LOGIN = '/main-dashboard-with-inventory-navigation';

/** Longer than any real page address; a longer value is refused, not truncated. */
export const MAX_NEXT_LENGTH = 2000;

const PARSE_BASE = 'http://next.invalid';

function passes(s: string): boolean {
  if (!s.startsWith('/')) return false;
  if (s.startsWith('//') || s.startsWith('/\\')) return false;
  if (s.includes('\\')) return false;
  if (/\s/.test(s)) return false;
  // Numeric comparisons, so no literal control byte ever lands in this source file.
  for (const ch of s) {
    const code = ch.charCodeAt(0);
    if (code < 32 || code === 127) return false;
  }
  const colon = s.indexOf(':');
  const slash = s.indexOf('/');
  if (colon !== -1 && (slash === -1 || colon < slash)) return false;
  return true;
}

/** Where to go after sign-in (or a forced flow): `raw` when it is a safe same-origin path. */
export function safeNextPath(raw: string | null | undefined): string {
  if (typeof raw !== 'string' || raw === '' || raw.length > MAX_NEXT_LENGTH) {
    return DEFAULT_POST_LOGIN;
  }
  if (!passes(raw)) return DEFAULT_POST_LOGIN;
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    // A malformed escape is itself disqualifying.
    return DEFAULT_POST_LOGIN;
  }
  if (!passes(decoded)) return DEFAULT_POST_LOGIN;
  try {
    const url = new URL(raw, PARSE_BASE);
    if (url.origin !== PARSE_BASE) return DEFAULT_POST_LOGIN;
    if (url.pathname === '/login' || url.pathname.startsWith('/login/')) return DEFAULT_POST_LOGIN;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return DEFAULT_POST_LOGIN;
  }
}

/** `next` from a query string (`?a=b&next=…`), checked. */
export function safeNextFromSearch(search: string): string {
  return safeNextPath(new URLSearchParams(search).get('next'));
}

/**
 * The `next` to carry for the page the user asked for, or null when there is nothing worth
 * returning to (the default page itself, or a value that fails the check).
 */
export function nextParamFor(pathWithQuery: string): string | null {
  const safe = safeNextPath(pathWithQuery);
  return safe === DEFAULT_POST_LOGIN ? null : safe;
}
